import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { COPY as copy } from "../locales/home";
import "../styles/home.css";
import "../styles/rf-home.css";
import { playableUrl } from "../playableBase";

const HELPSHIFT_PLATFORM_ID =
  "luckyspin_platform_20260925194007053-b0662ba79ab539d";
const HELPSHIFT_SCRIPT_ID = "hs-chat";
const CONTACT_EMAIL = "hello@luckyspingames.com";
// Neutral opener sent once per page load so the LSG Helpshift bot's Games /
// Company menu appears immediately when a visitor opens support, without
// asking them to type anything first. Helpshift only honors
// setInitialUserMessage before a conversation exists, but we still guard it
// ourselves so a second "Get support" click in the same visit can never
// send it twice or start a second conversation.
const HELPSHIFT_INITIAL_MESSAGE = "I'd like to contact Lucky Spin Games.";
let helpshiftInitialMessageSent = false;

const ASSET = (name) => `${process.env.PUBLIC_URL}/lsg/${name}`;

// All store / social links live here.
const LINKS = {
  rfIos: "https://apps.apple.com/us/app/rare-find-merge-mystery/id6766818537",
  rfAndroid:
    "https://play.google.com/store/apps/details?id=com.luckyspingames.mth2",
  rfAmazon: "https://www.amazon.com/gp/product/B0GXPVVN4F",
  rfFacebook: "https://www.facebook.com/profile.php?id=61591515833240",
  mthIos: "https://apps.apple.com/us/app/merge-treasure-hunt-combine/id1660026803",
  mthAndroid:
    "https://play.google.com/store/apps/details?id=com.luckyspingames.merge2",
  mthAmazon:
    "https://www.amazon.com/Lucky-Spin-Games-Treasure-Hunt%EF%BC%8DMatch/dp/B0BR3R4CRH",
  mthMicrosoft: "https://apps.microsoft.com/detail/9p9ddn6hpfw8?hl=en-US&gl=US",
  mthFacebook: "https://www.facebook.com/mergetreasurehunt/",
};

// LAUNCH SWITCHES — one per store.
// "live" = that store's badge opens the store listing.
// "soon" = that store's badge opens the "coming soon" dialog instead.
// Everything else follows from these three lines: the hero pill, the dialog,
// the All-our-games card and the tab title. A store only counts as live if its
// link in LINKS above is filled in.
const RF_STORE_STATUS = {
  appStore: "soon",
  googlePlay: "live",
  amazon: "live",
};

// Badge order is Apple first (Apple's guideline), then Google, then Amazon.
const RF_STORES = [
  { key: "appStore", name: "App Store", phrase: "the App Store", url: LINKS.rfIos, badge: "brand/badge-app-store.svg", alt: "Download on the App Store" },
  { key: "googlePlay", name: "Google Play", phrase: "Google Play", url: LINKS.rfAndroid, badge: "brand/badge-google-play.svg", alt: "Get it on Google Play" },
  { key: "amazon", name: "Amazon Appstore", phrase: "the Amazon Appstore", url: LINKS.rfAmazon, badge: "brand/badge-amazon-appstore.svg", alt: "Available at Amazon Appstore" },
].map((s) => ({ ...s, live: RF_STORE_STATUS[s.key] === "live" && Boolean(s.url) }));
const RF_LIVE = RF_STORES.filter((s) => s.live);
const RF_SOON = RF_STORES.filter((s) => !s.live);
const RARE_FIND_IS_LIVE = RF_SOON.length === 0; // live on every store
const RARE_FIND_ANY_LIVE = RF_LIVE.length > 0; // live on at least one

// "the App Store", "the App Store and Google Play", "A, B and C".
const listPhrases = (stores) => {
  const p = stores.map((s) => s.phrase);
  return p.length <= 1 ? p[0] || "" : `${p.slice(0, -1).join(", ")} and ${p[p.length - 1]}`;
};
const fill = (text, vars) => text.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");
const LIVE_PHRASE = listPhrases(RF_LIVE);
const SOON_PHRASE = listPhrases(RF_SOON);
const DIALOG_TITLE = RARE_FIND_IS_LIVE
  ? copy.liveTitle
  : RARE_FIND_ANY_LIVE
  ? fill(copy.partialTitle, { live: LIVE_PHRASE })
  : copy.soonTitle;
const DIALOG_BODY = RARE_FIND_IS_LIVE
  ? copy.liveBody
  : RARE_FIND_ANY_LIVE
  ? fill(copy.partialBody, { soon: SOON_PHRASE })
  : fill(copy.soonBody, { stores: listPhrases(RF_STORES) });

// Art. Files under lsg/rf/ are swapped for final Figma exports.
const ART = {
  rfLogo: ASSET("rarefind-logo.png"),
  keyart: ASSET("rf/rf-keyart.jpg"),
  santoriniSketch: ASSET("rf/santorini-sketch.jpg"),
  santoriniColor: ASSET("rf/santorini-color.jpg"),
  gardenSketch: ASSET("rf/glass-garden-sketch.jpg"),
  gardenColor: ASSET("rf/glass-garden-color.jpg"),
  iris: ASSET("rf/iris.jpg"),
  claire: ASSET("rf/claire.jpg"),
  jake: ASSET("rf/jake.jpg"),
  shots: [1, 2, 3, 4, 5, 6].map((n) => ASSET(`rf/screenshot-${n}.jpg`)),
};

// Postcards and screenshots only render once their image file exists in
// public/lsg/rf/, so art can be dropped in later with no code change.
const POSTCARDS = [
  { n: 1, name: "The Grand Express", note: "A private carriage, reserved under the initials G.M.", img: ASSET("rf/postcard-grand-express.jpg"), tilt: -2.5, pos: "center 27%" },
  { n: 2, name: "Santorini", note: "Where the white houses meet the sea.", img: ASSET("rf/santorini-color.jpg"), tilt: 1.5, pos: "center 38%" },
  { n: 4, name: "The Castle Village", note: "You can see the castle from every street.", img: ASSET("rf/postcard-castle-village.jpg"), tilt: -1, pos: "center 18%" },
  { n: 6, name: "The Glass Garden", note: "Glass, plants, and sunlight.", img: ASSET("rf/glass-garden-color.jpg"), tilt: 2, pos: "center 40%" },
];

// The three character-mode playable builds, published from the /playable
// review console (public/playable/<slug>/index.html — already live there).
const PLAYABLE_CHARACTERS = [
  { slug: "iris", nameKey: "castIris", titleKey: "playIris", img: ART.iris },
  { slug: "claire", nameKey: "castClaire", titleKey: "playClaire", img: ART.claire },
  { slug: "jake", nameKey: "castJake", titleKey: "playJake", img: ART.jake },
];

const SHOT_CAPTIONS = ["Merge", "Find", "Discover", "Restore", "Explore", "Rare Find"];

// From the game's own script (Intro, Part 2), lightly trimmed.
const CHAT = [
  { from: "iris", text: "ok don't freak out" },
  { from: "iris", text: "I'm on a plane. somewhere over Greenland right now, I think." },
  { from: "claire", text: "wait WHAT. a plane to WHERE?? Iris it's Tuesday" },
  { from: "iris", text: "a package came to my desk. one of grandpa\u2019s sketchbooks. a note, and a ticket to Vienna. so here I am ✈️" },
  { from: "jake", text: "you actually did it. you just… went. I'm so proud of you 🥹" },
  { from: "claire", text: "…ok. then you text us constantly. every step." },
  { from: "iris", text: "deal. look what's on the next page 👇" },
  { from: "iris", photo: true },
  { from: "jake", text: "a TRAIN. ok. where does it go?" },
  { from: "iris", text: "guess I'm about to find out 🚂" },
];

const CHAT_PEOPLE = {
  iris: { name: "Iris", img: ART.iris },
  claire: { name: "Claire", img: ART.claire },
  jake: { name: "Jake", img: ART.jake },
};

const CAST = [
  { key: "iris", name: copy.castIris, role: copy.castIrisRole, quote: copy.castIrisQuote, bio: copy.castIrisBio, img: ART.iris },
  { key: "claire", name: copy.castClaire, role: copy.castClaireRole, quote: copy.castClaireQuote, bio: copy.castClaireBio, img: ART.claire },
  { key: "jake", name: copy.castJake, role: copy.castJakeRole, quote: copy.castJakeQuote, bio: copy.castJakeBio, img: ART.jake },
  { key: "unknown", name: copy.castUnknown, role: copy.castUnknownRole, quote: copy.castUnknownQuote, bio: copy.castUnknownBio, img: ASSET("rf/mystery.jpg"), evidence: true },
];

// Portraits open a short bio in the panel below them.
function Cast() {
  const [open, setOpen] = useState(null);
  const active = CAST.find((c) => c.key === open);
  return (
    <div className="rf-cast-block">
      <ul className="rf-cast" role="tablist" aria-label="Rare Find characters">
        {CAST.map((c) => (
          <li key={c.key}>
            <button
              type="button"
              role="tab"
              aria-selected={open === c.key}
              aria-controls="rf-cast-bio"
              className={`rf-cast-btn ${open === c.key ? "on" : ""}`}
              onClick={() => setOpen(open === c.key ? null : c.key)}
            >
              <img src={c.img} alt="" onError={hideOnError} />
              <span>{c.name}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="rf-cast-bio" id="rf-cast-bio" role="tabpanel">
        {active ? (
          <>
            <p className="rf-cast-role">{active.role}</p>
            <p className={`rf-cast-quote ${active.evidence ? "evidence" : ""}`}>
              {active.evidence ? active.quote : `\u201c${active.quote}\u201d`}
            </p>
            <p>{active.bio}</p>
          </>
        ) : (
          <p className="rf-cast-hint">{copy.castHint}</p>
        )}
      </div>
    </div>
  );
}

/* ---------- Helpshift ---------- */
function ensureHelpshift() {
  if (typeof window === "undefined") return;
  window.helpshiftConfig = {
    platformId: HELPSHIFT_PLATFORM_ID,
    domain: "luckyspin",
    language: "en",
  };
  if (typeof window.Helpshift !== "function") {
    const helpshift = function () {
      helpshift.q.push(arguments);
    };
    helpshift.q = [];
    window.Helpshift = helpshift;
  }
  if (document.getElementById(HELPSHIFT_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = HELPSHIFT_SCRIPT_ID;
  script.async = true;
  script.src = "https://webchat.helpshift.com/latest/webChat.js";
  script.addEventListener("load", () => window.Helpshift && window.Helpshift("init"));
  document.head.appendChild(script);
}

/* ---------- Helpers ---------- */
function usePlatform() {
  return useMemo(() => {
    if (typeof navigator === "undefined") return "desktop";
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua)) return "android";
    if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
    return "desktop";
  }, []);
}

function useMediaQuery(query) {
  const get = () => typeof window !== "undefined" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, [query]);
  return matches;
}

// Returns the subset of image URLs that actually load, in their original order.
function useAvailableImages(urls) {
  const [ok, setOk] = useState([]);
  useEffect(() => {
    let alive = true;
    urls.forEach((url) => {
      const img = new Image();
      img.onload = () => alive && setOk((prev) => (prev.includes(url) ? prev : [...prev, url]));
      img.src = url;
    });
    return () => {
      alive = false;
    };
  }, [urls]);
  return urls.filter((u) => ok.includes(u));
}

// Hide an <img> whose file isn't there yet so placeholders stay tidy.
const hideOnError = (e) => {
  e.currentTarget.style.visibility = "hidden";
};

// Official badge artwork from Apple, Google and Amazon. Do not redraw, recolour
// or alter these files. Apple requires the black badge when badges for other
// stores appear alongside it, and asks that it come first in the lineup.
// Google requires its badge to be at least as large as any other store's —
// they are rendered at matching heights, so keep it that way. If a badge file
// is missing, the store name is shown as text instead of a broken image.
function StoreBadge({ store, compact, onComingSoon }) {
  const [broken, setBroken] = useState(false);
  const soon = !store.live;
  const linkProps = soon
    ? {
        href: "#rare-find",
        onClick: (e) => {
          e.preventDefault();
          if (onComingSoon) onComingSoon();
        },
        "aria-haspopup": "dialog",
        "aria-label": `${store.name}, coming soon`,
      }
    : { href: store.url, target: "_blank", rel: "noopener noreferrer" };
  return (
    <a className={`rf-store ${compact ? "compact" : ""} ${soon ? "soon" : ""}`} {...linkProps}>
      {broken ? (
        <span className="rf-store-text">{store.name}</span>
      ) : (
        <img src={ASSET(store.badge)} alt={store.alt} onError={() => setBroken(true)} />
      )}
    </a>
  );
}

// Every store always shows: the site is read by publishers and investors, not
// just players. (usePlatform() is passed in but deliberately unused.)
function StoreBadges({ stores = RF_STORES, compact, onComingSoon }) {
  return (
    <div className="rf-stores">
      {stores.map((s) => (
        <StoreBadge key={s.key} store={s} compact={compact} onComingSoon={onComingSoon} />
      ))}
    </div>
  );
}

/* Sketch-to-color: the color layer wipes in as the panel scrolls through view. */
function RevealPanel({ sketch, color, name, reduceMotion }) {
  const ref = useRef(null);
  const [progress, setProgress] = useState(reduceMotion ? 1 : 0);

  useEffect(() => {
    if (reduceMotion) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the panel's top reaches 85% of the viewport, 1 when its bottom reaches 55%.
      const start = vh * 0.85;
      const end = vh * 0.55 - rect.height;
      const p = (start - rect.top) / (start - end);
      setProgress(Math.min(1, Math.max(0, p)));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [reduceMotion]);

  const pct = Math.round(progress * 100);
  return (
    <figure className="rf-reveal-panel" ref={ref}>
      <img className="rf-reveal-sketch" src={sketch} alt={`${name}, pencil sketch`} onError={hideOnError} />
      <img
        className="rf-reveal-color"
        src={color}
        alt={`${name}, restored in full color`}
        style={{ clipPath: `inset(0 0 ${100 - pct}% 0)` }}
        onError={hideOnError}
      />
      <div className="rf-reveal-line" style={{ top: `${pct}%`, opacity: pct > 0 && pct < 100 ? 1 : 0 }} />
      <figcaption>
        <span className="rf-reveal-name">{name}</span>
        <span className="rf-reveal-meter" aria-hidden="true">
          <span style={{ width: `${pct}%` }} />
        </span>
      </figcaption>
    </figure>
  );
}

/* Playable demo: pick Iris, Claire or Jake's portrait to load their version
   of the game. Each build (~4 MB) only loads once its portrait is tapped. */
function PlayableDemo({ onComingSoon }) {
  const [active, setActive] = useState(null);
  const activeGame = PLAYABLE_CHARACTERS.find((g) => g.slug === active);

  // The demo's end-card CTA posts here instead of opening a store. We show the
  // same launch dialog the store badges show, so it always matches the current
  // store status: a download button for each live store, "coming soon" for the
  // rest, and Facebook. Origin is checked because the demos are served from S3.
  const comingSoonRef = useRef(onComingSoon);
  comingSoonRef.current = onComingSoon;
  useEffect(() => {
    const onMessage = (e) => {
      if (!/^https:\/\/lsg-playable-builds\.s3[.-]/.test(e.origin)) return;
      const d = e.data;
      if (d && d.source === "rf-playable" && d.action === "cta") comingSoonRef.current();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  return (
    <section className="rf-play" id="try-it">
      <div className="lsg-container rf-play-grid">
        <div>
          <p className="rf-hero-kicker">{copy.playKicker}</p>
          <h2 className="rf-h2">{copy.playTitle}</h2>
          <p className="rf-lede">{copy.playBody}</p>
          <div className="rf-play-picker" role="group" aria-label={copy.playPickHint}>
            {PLAYABLE_CHARACTERS.map((g) => (
              <button
                key={g.slug}
                type="button"
                className={`rf-play-avatar${active === g.slug ? " is-active" : ""}`}
                onClick={() => setActive(g.slug)}
              >
                <img src={g.img} alt="" onError={hideOnError} />
                <span>{copy[g.nameKey]}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="rf-play-phone">
          {activeGame ? (
            <>
              <iframe
                key={activeGame.slug}
                title={`Rare Find playable demo — ${copy[activeGame.nameKey]}`}
                src={playableUrl(activeGame.slug, "index.html")}
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                allow="autoplay"
              />
              <button type="button" className="rf-play-close" onClick={() => setActive(null)} aria-label={copy.playClose}>
                ×
              </button>
            </>
          ) : (
            <div className="rf-play-empty">
              <img src={ART.shots[0]} alt="" onError={hideOnError} />
              <p>{copy.playPickHint}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// If public/lsg/rf/rf-chat.mp4 exists, it replaces the mocked-up chat.
const CHAT_VIDEO = ASSET("rf/rf-chat.mp4");
function useFileIsVideo(url) {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch(url, { method: "HEAD" })
      .then((r) => alive && setOk(r.ok && /^video\//.test(r.headers.get("content-type") || "")))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [url]);
  return ok;
}

// Plays only while on screen, so the conversation starts where the reader does.
function ChatVideo({ reduceMotion }) {
  const ref = useRef(null);
  useEffect(() => {
    const video = ref.current;
    if (!video || reduceMotion) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const played = video.play();
          if (played && played.catch) played.catch(() => {});
        } else {
          video.pause();
          if (entry.boundingClientRect.top > 0) video.currentTime = 0;
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [reduceMotion]);
  return (
    <div className="rf-play-phone rf-chat-video">
      <video ref={ref} src={CHAT_VIDEO} controls={reduceMotion} muted loop playsInline preload="auto" aria-label="Rare Find group chat" />
    </div>
  );
}

function GroupChat({ reduceMotion }) {
  const hasVideo = useFileIsVideo(CHAT_VIDEO);
  const ref = useRef(null);
  const [shown, setShown] = useState(reduceMotion ? CHAT.length : 0);

  useEffect(() => {
    if (reduceMotion || !ref.current) return undefined;
    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        let i = 0;
        const next = () => {
          i += 1;
          setShown(i);
          if (i < CHAT.length) timer = setTimeout(next, 1100);
        };
        timer = setTimeout(next, 250);
      },
      { threshold: 0.35 }
    );
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [reduceMotion]);

  if (hasVideo) return <ChatVideo reduceMotion={reduceMotion} />;

  return (
    <div className="rf-chat" ref={ref} aria-label="Rare Find group chat preview">
      <div className="rf-chat-head">
        <span className="rf-chat-faces">
          {["claire", "jake", "iris"].map((p) => (
            <img key={p} src={CHAT_PEOPLE[p].img} alt="" onError={hideOnError} />
          ))}
        </span>
        <span>
          <strong>Iris, Claire, Jake</strong>
          <small>Group chat</small>
        </span>
      </div>
      <ol className="rf-chat-log">
        {CHAT.slice(0, shown).map((m, i) => {
          const person = CHAT_PEOPLE[m.from];
          const prev = CHAT[i - 1];
          const firstInRun = !prev || prev.from !== m.from;
          return (
            <li key={i} className={`rf-msg ${m.from === "iris" ? "mine" : "theirs"}`}>
              {m.from !== "iris" && firstInRun && <span className="rf-msg-name">{person.name}</span>}
              {m.photo ? (
                <img className="rf-msg-photo" src={ASSET("rf/grand-express-sketch.jpg")} alt="Unfinished pencil sketch of a train carriage" onError={hideOnError} />
              ) : (
                <p>{m.text}</p>
              )}
            </li>
          );
        })}
        {shown < CHAT.length && (
          <li className="rf-msg typing" aria-hidden="true">
            <p>
              <i />
              <i />
              <i />
            </p>
          </li>
        )}
      </ol>
    </div>
  );
}

function Home() {
  const platform = usePlatform();
  const isPortrait = useMediaQuery("(max-width: 760px)");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [soonOpen, setSoonOpen] = useState(false);
  const [heroPlaying, setHeroPlaying] = useState(false);
  const heroVideoRef = useRef(null);
  // The poster is painted by CSS on the section itself, so the art is always
  // visible even if the video fails; the video fades in only once it plays.
  const heroBase = "rf/rf-hero-16x9";
  const postcardUrls = useMemo(() => POSTCARDS.map((c) => c.img), []);
  const readyPostcards = useAvailableImages(postcardUrls);
  const readyShots = useAvailableImages(ART.shots);
  const postcards = POSTCARDS.filter((c) => readyPostcards.includes(c.img));
  const year = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    document.documentElement.lang = "en";
    // Driven by the launch flag, not hardcoded: the tab title is the one piece
    // of launch-state copy a visitor sees before the page even renders.
    document.title = RARE_FIND_ANY_LIVE
      ? "LSG — Play Rare Find now"
      : "LSG — Lucky Spin Games";
    try {
      localStorage.removeItem("lsg-lang");
    } catch (e) {
      // ignore
    }
    ensureHelpshift();
  }, []);

  useEffect(() => {
    if (!trailerOpen && !soonOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setTrailerOpen(false);
      setSoonOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [trailerOpen, soonOpen]);

  const openSupport = () => {
    ensureHelpshift();
    if (window.Helpshift) {
      if (!helpshiftInitialMessageSent) {
        helpshiftInitialMessageSent = true;
        window.Helpshift("setInitialUserMessage", HELPSHIFT_INITIAL_MESSAGE);
      }
      window.Helpshift("open");
      return;
    }
    window.location.href = `mailto:${CONTACT_EMAIL}`;
  };

  const trailer = isPortrait ? "rf/rf-trailer-9x16.mp4" : "rf/rf-trailer-16x9.mp4";

  return (
    <div className="lsg-home rf-home" id="top">
      {/* NAV */}
      <nav className="lsg-nav rf-nav">
        <div className="lsg-container lsg-nav-inner">
          <a href="#top" className="rf-nav-logo" aria-label="Lucky Spin Games home">
            <img src={ASSET("brand/lsg-horizontal-dark.svg")} alt="Lucky Spin Games" width="456" height="119" />
          </a>
          <ul className="lsg-nav-links rf-nav-links">
            <li><a href="#games">{copy.navGames}</a></li>
            <li><a href="#about">{copy.navAbout}</a></li>
            <li><a href="#support">{copy.navSupport}</a></li>
            <li><a href={`mailto:${CONTACT_EMAIL}`}>{copy.navContact}</a></li>
          </ul>
          <a href="#rare-find" className="lsg-nav-cta">{copy.navPlay}</a>
        </div>
      </nav>

      {/* RARE FIND HERO */}
      <header className="rf-hero" id="rare-find">
        <div className="rf-hero-art">
        <img className="rf-hero-media rf-hero-still" src={ASSET(`${heroBase}-poster.jpg`)} alt="" />
        {!reduceMotion && (
          <video
            ref={heroVideoRef}
            className={`rf-hero-media ${heroPlaying ? "on" : ""}`}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            onPlaying={() => setHeroPlaying(true)}
            onError={() => setHeroPlaying(false)}
          >
            <source src={ASSET(`${heroBase}.mp4`)} type="video/mp4" />
          </video>
        )}
        <div className="rf-hero-shade" />
        </div>
        <div className="lsg-container rf-hero-inner">
          <img className="rf-hero-logo" src={ART.rfLogo} alt="Rare Find: Merge & Discover" />
          {RARE_FIND_IS_LIVE ? (
            <p className="rf-hero-kicker">{copy.rfHeroKicker}</p>
          ) : (
            <p className="rf-hero-kicker">
              <button type="button" className="rf-soon-tag" onClick={() => setSoonOpen(true)}>
                {RARE_FIND_ANY_LIVE ? fill(copy.partialTag, { live: LIVE_PHRASE }) : copy.soonTag}
              </button>
            </p>
          )}
          <h1 className="rf-hero-title">{copy.rfHeroTitle}</h1>
          <p className="rf-hero-body">{copy.rfHeroBody}</p>
          <div className="rf-hero-actions">
            <StoreBadges platform={platform} onComingSoon={() => setSoonOpen(true)} />
            <button type="button" className="rf-trailer-btn" onClick={() => setTrailerOpen(true)}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
              {copy.rfTrailer}
            </button>
          </div>
        </div>
      </header>

      {/* SKETCH TO COLOR */}
      <section className="rf-reveal">
        <div className="lsg-container">
          <div className="rf-split-head">
            <h2 className="rf-h2">{copy.revealTitle}</h2>
            <p className="rf-lede">{copy.revealBody}</p>
          </div>
          <div className="rf-reveal-grid">
            <RevealPanel sketch={ART.santoriniSketch} color={ART.santoriniColor} name="Santorini" reduceMotion={reduceMotion} />
            <RevealPanel sketch={ART.gardenSketch} color={ART.gardenColor} name="The Glass Garden" reduceMotion={reduceMotion} />
          </div>
        </div>
      </section>

      {/* POSTCARDS */}
      {postcards.length > 0 && (
        <section className="rf-postcards">
          <div className="lsg-container">
            <h2 className="rf-h2">{copy.postcardsTitle}</h2>
            <p className="rf-lede rf-postcards-lede">{copy.postcardsBody}</p>
            <div className="rf-postcard-row">
              {postcards.map((c) => (
                <figure className="rf-postcard" key={c.name} style={{ "--tilt": `${c.tilt}deg` }}>
                  <div className="rf-postcard-img">
                    <img src={c.img} alt={c.name} style={{ objectPosition: c.pos }} />
                  </div>
                  <figcaption>
                    <small>Nº {c.n}</small>
                    {c.name}
                    <em>{c.note}</em>
                  </figcaption>
                </figure>
              ))}
            </div>
            <p className="rf-postcards-more">{copy.postcardsMore}</p>
          </div>
        </section>
      )}

      {/* GROUP CHAT */}
      <section className="rf-story">
        <div className="lsg-container rf-story-grid">
          <div className="rf-story-copy">
            <h2 className="rf-h2">{copy.chatTitle}</h2>
            <p className="rf-lede">{copy.chatBody}</p>
            <Cast />
          </div>
          <GroupChat reduceMotion={reduceMotion} />
        </div>
      </section>

      {/* PLAYABLE DEMO */}
      <PlayableDemo onComingSoon={() => setSoonOpen(true)} />

      {/* SCREENSHOTS */}
      {readyShots.length > 0 && (
        <section className="rf-shots">
          <div className="lsg-container">
            <h2 className="rf-h2 rf-shots-title">{copy.shotsTitle}</h2>
          </div>
          <div className="rf-shots-track" tabIndex={0} aria-label="Rare Find screenshots">
            {readyShots.map((src) => (
              <img key={src} src={src} alt={`Rare Find screenshot: ${SHOT_CAPTIONS[ART.shots.indexOf(src)]}`} />
            ))}
          </div>
        </section>
      )}

      {/* ABOUT ("Why we made Rare Find") */}
      <section className="rf-about" id="about">
        <div className="lsg-container rf-about-grid">
          <h2 className="rf-h2">{copy.aboutTitle}</h2>
          <div className="rf-about-body">
            <p>{copy.aboutBody1}</p>
            <p>{copy.aboutBody2}</p>
            <p>{copy.aboutBody3}</p>
            <a className="lsg-btn-primary" href={`mailto:${CONTACT_EMAIL}`}>{copy.aboutCta}</a>
          </div>
        </div>
      </section>

      {/* RF COMMUNITY (Facebook) */}
      <section className="rf-community rf-community-solo">
        <div className="lsg-container">
          <div className="rf-community-grid is-solo">
            <div className="rf-community-card">
              <h3>{copy.rfCommunity}</h3>
              <p>{copy.rfCommunityBody}</p>
              <a href={LINKS.rfFacebook} target="_blank" rel="noopener noreferrer" className="lsg-fb-btn">
                <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                {copy.joinFacebook}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PLAY NOW */}
      <section className="rf-playnow">
        <div className="lsg-container rf-playnow-inner">
          <img className="rf-playnow-logo" src={ART.rfLogo} alt="" />
          <StoreBadges platform={platform} compact onComingSoon={() => setSoonOpen(true)} />
        </div>
      </section>

      {/* MERGE TREASURE HUNT */}
      <section className="rf-mth">
        <div className="lsg-container rf-mth-grid">
          <div className="rf-mth-copy">
            <img className="rf-mth-icon" src={ASSET("mth/mth-icon.png")} alt="" width="96" height="96" />
            <p className="rf-hero-kicker">{copy.mthKicker}</p>
            <h2 className="rf-h2">{copy.mthName}</h2>
            <p className="rf-lede">{copy.mthDesc}</p>
            <div className="rf-pill-row">
              <a href={LINKS.mthIos} target="_blank" rel="noopener noreferrer" className="lsg-pill-link">App Store</a>
              <a href={LINKS.mthAndroid} target="_blank" rel="noopener noreferrer" className="lsg-pill-link">Google Play</a>
              <a href={LINKS.mthAmazon} target="_blank" rel="noopener noreferrer" className="lsg-pill-link">Amazon</a>
              <a href={LINKS.mthMicrosoft} target="_blank" rel="noopener noreferrer" className="lsg-pill-link">Microsoft</a>
            </div>
          </div>
          <div>
            <div className="rf-stats rf-stats-2x2">
              <div><strong>4.8<span>★</span></strong><small>{copy.statRating}</small></div>
              <div><strong>100K+</strong><small>{copy.statDownloads}</small></div>
              <div><strong>52K+</strong><small>{copy.statRatings}</small></div>
              <div><strong>4</strong><small>{copy.statPlatforms}</small></div>
            </div>
            <p className="rf-stats-foot">{copy.statFootnote}</p>
          </div>
        </div>
        <div className="rf-mth-shots" tabIndex={0} aria-label="Merge Treasure Hunt screenshots">
          {["loading", "merge", "discover", "restore", "explore"].map((n) => (
            <img key={n} src={ASSET(`mth/mth-${n}.jpg`)} alt={`Merge Treasure Hunt: ${n} screen`} loading="lazy" />
          ))}
        </div>
      </section>

      {/* MTH COMMUNITY (Facebook) */}
      <section className="rf-community rf-community-solo">
        <div className="lsg-container">
          <div className="rf-community-grid is-solo">
            <div className="rf-community-card">
              <h3>{copy.mthCommunity}</h3>
              <p>{copy.mthCommunityBody}</p>
              <a href={LINKS.mthFacebook} target="_blank" rel="noopener noreferrer" className="lsg-fb-btn">
                <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                {copy.joinFacebook}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ALL GAMES */}
      <section className="lsg-games rf-games" id="games">
        <div className="lsg-container">
          <h2 className="rf-h2">{copy.gamesTitle}</h2>
          <div className="lsg-games-grid">
            <article className="lsg-game-card">
              <div className="lsg-game-media">
                <img src={ART.keyart} alt="Rare Find" />
                <span className={`lsg-game-badge ${RARE_FIND_ANY_LIVE ? "live" : "dev"}`}>
                  {RARE_FIND_IS_LIVE
                    ? copy.outNow
                    : RARE_FIND_ANY_LIVE
                    ? fill(copy.partialTag, { live: LIVE_PHRASE })
                    : copy.comingSoon}
                </span>
              </div>
              <div className="lsg-game-body">
                <h3 className="lsg-game-title">{copy.rareName}</h3>
                <p className="lsg-game-desc">{copy.rareDesc}</p>
                <div className="lsg-store-row">
                  {RF_LIVE.map((s) => (
                    <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" className="lsg-store-link">{s.name}</a>
                  ))}
                  {RF_SOON.length > 0 && (
                    <button type="button" className="lsg-store-link" onClick={() => setSoonOpen(true)}>
                      {RARE_FIND_ANY_LIVE ? copy.moreStoresSoon : copy.soonTag}
                    </button>
                  )}
                </div>
              </div>
            </article>
            <article className="lsg-game-card">
              <div className="lsg-game-media">
                <img src={ASSET("mth/mth-loading.jpg")} alt="Merge Treasure Hunt" style={{ objectPosition: "center 22%" }} />
                <span className="lsg-game-badge live">{copy.liveNow}</span>
              </div>
              <div className="lsg-game-body">
                <h3 className="lsg-game-title">{copy.mthName}</h3>
                <p className="lsg-game-desc">{copy.mthDesc}</p>
                <div className="lsg-store-row">
                  <a href={LINKS.mthIos} target="_blank" rel="noopener noreferrer" className="lsg-store-link">App Store</a>
                  <a href={LINKS.mthAndroid} target="_blank" rel="noopener noreferrer" className="lsg-store-link">Google Play</a>
                </div>
              </div>
            </article>
            <article className="lsg-game-card">
              <div className="lsg-game-media">
                <img src={ASSET("cats-screen.jpg")} alt="Untitled merge project" />
                <span className="lsg-game-badge dev">{copy.inDevelopment}</span>
              </div>
              <div className="lsg-game-body">
                <h3 className="lsg-game-title">{copy.catsName}</h3>
                <p className="lsg-game-desc">{copy.catsDesc}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* SUPPORT (anchor #support is used by the app store listings) */}
      <section className="rf-support" id="support">
        <div className="lsg-container rf-support-inner">
          <div>
            <h2 className="rf-h2">{copy.supportTitle}</h2>
            <p className="rf-lede">{copy.supportBody}</p>
          </div>
          <button type="button" className="lsg-support-btn" onClick={openSupport}>
            {copy.supportButton}
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lsg-footer">
        <div className="lsg-container">
          <div className="lsg-footer-grid">
            <div>
              <img className="rf-footer-logo" src={ASSET("brand/lsg-stacked-dark.svg")} alt="" />
              <p className="lsg-footer-blurb">{copy.footerBlurb}</p>
            </div>
            <div className="lsg-footer-col">
              <p className="lsg-footer-col-title">{copy.footerGames}</p>
              <a href="#rare-find">{copy.rareName}</a>
              <a href="#games">{copy.mthName}</a>
            </div>
            <div className="lsg-footer-col">
              <p className="lsg-footer-col-title">{copy.footerCompany}</p>
              <a href="#about">{copy.footerAbout}</a>
              <a href="#support">{copy.footerSupport}</a>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </div>
            <div className="lsg-footer-col">
              <p className="lsg-footer-col-title">{copy.footerLegal}</p>
              <Link to="/privacy-policy">{copy.footerPrivacy}</Link>
              <Link to="/terms">{copy.footerTerms}</Link>
            </div>
          </div>
          <div className="lsg-footer-bottom">{copy.footerCopy.replace("{year}", String(year))}</div>
        </div>
      </footer>

      {/* LAUNCH DIALOG — opened by a "soon" badge, the hero pill, the games card
          or the demo CTA. Never opens on its own. */}
      {soonOpen && (
        <div className="rf-modal" role="dialog" aria-modal="true" aria-label={DIALOG_TITLE} onClick={() => setSoonOpen(false)}>
          <div className="rf-soon" onClick={(e) => e.stopPropagation()}>
            <img className="rf-soon-logo" src={ART.rfLogo} alt="" />
            <h2>{DIALOG_TITLE}</h2>
            <p>{DIALOG_BODY}</p>
            <div className="rf-soon-actions">
              {RARE_FIND_ANY_LIVE && <StoreBadges stores={RF_LIVE} compact />}
              <a className="lsg-fb-btn" href={LINKS.rfFacebook} target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                {copy.soonFollow}
              </a>
              <button type="button" className="rf-soon-dismiss" onClick={() => setSoonOpen(false)}>
                {copy.soonDismiss}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TRAILER */}
      {trailerOpen && (
        <div className="rf-modal" role="dialog" aria-modal="true" aria-label="Rare Find trailer" onClick={() => setTrailerOpen(false)}>
          <div className={`rf-modal-frame ${isPortrait ? "portrait" : ""}`} onClick={(e) => e.stopPropagation()}>
            <video src={ASSET(trailer)} controls autoPlay playsInline />
            <button type="button" className="rf-modal-close" onClick={() => setTrailerOpen(false)} aria-label={copy.closeTrailer}>
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
